import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Loader2 } from "lucide-react";

export interface UploadedFile {
  name: string;
  content: string;
}

interface ConfigurationFilesProps {
  ctaLabel?: string;
  uploadedFiles: UploadedFile[];
  onUploadedFilesChange: (files: UploadedFile[]) => void;
  disabled: boolean;
}

export function ConfigurationFiles({
  ctaLabel = "Add Files",
  uploadedFiles,
  onUploadedFilesChange,
  disabled,
}: ConfigurationFilesProps) {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [isAddingFiles, setIsAddingFiles] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setIsAddingFiles(true);
      const newFiles = Array.from(event.target.files);
      const existingFileNames = new Set(uploadedFiles.map((f) => f.name));
      const uniqueNewFiles = newFiles.filter(
        (f) => !existingFileNames.has(f.name),
      );

      const readPromises = uniqueNewFiles.map((file) => {
        return new Promise<UploadedFile>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const content = e.target?.result as string;
            resolve({ name: file.name, content });
          };
          reader.onerror = reject;
          reader.readAsText(file);
        });
      });

      Promise.all(readPromises)
        .then((newUploadedFiles) => {
          onUploadedFilesChange([...uploadedFiles, ...newUploadedFiles]);
        })
        .catch((err) => {
          console.error("Error reading files:", err);
        })
        .finally(() => {
          setIsAddingFiles(false);
          if (event.target) {
            event.target.value = "";
          }
        });
    }
  };

  const removeFile = (index: number) => {
    onUploadedFilesChange(uploadedFiles.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4 border-t pt-4">
      <h4 className="mb-1 text-sm font-medium text-muted-foreground">
        Configuration Files
      </h4>
      <p className="text-xs text-muted-foreground">
        Upload files to be included in your deployment.
      </p>
      <div className="space-y-4">
        {uploadedFiles.map((file, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className="flex-1">
              <Input
                placeholder="File Name"
                value={file.name}
                readOnly
                className="cursor-not-allowed font-mono"
                disabled={disabled}
              />
            </div>
            <div className="relative flex-1">
              <Textarea
                placeholder="File Content"
                value={file.content}
                readOnly
                onFocus={() => setFocusedIndex(index)}
                onBlur={() => setFocusedIndex(null)}
                className={`min-h-0 cursor-pointer resize-none bg-background font-mono transition-all duration-200 ${
                  focusedIndex === index
                    ? "absolute z-10 h-48 cursor-not-allowed"
                    : "absolute h-9 overflow-hidden"
                }`}
                disabled={disabled}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => removeFile(index)}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Label
          htmlFor="file-upload"
          className={`inline-flex h-10 w-full cursor-pointer items-center justify-center rounded-md border-2 border-primary bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 ${
            disabled || isAddingFiles ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          {isAddingFiles ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          {isAddingFiles ? "Adding..." : ctaLabel}
        </Label>
        <Input
          id="file-upload"
          type="file"
          multiple
          onChange={handleFileChange}
          disabled={disabled || isAddingFiles}
          className="hidden"
        />
      </div>
    </div>
  );
}
